import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, createFileRoute, useRouter } from '@tanstack/react-router'
import { z } from 'zod'
import { AlertTriangleIcon, SearchIcon } from 'lucide-react'
import { ORDER_STATES, STATE_LABELS, type OpsOrderRow } from '@hedgehog/shared'
import { opsApi } from '@/components/ops/api'
import { formatUsd } from '@/components/ops/format'
import { StateBadge } from '@/components/ops/state-badge'
import { ErrorState, LoadingState } from '@/components/app/states'
import { formatAge, formatDateTime } from '@/components/app/format'
import { useInterval } from '@/hooks/use-interval'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'
import { cn } from '@/lib/utils'

const PROFILES = ['classic', 'page_builder'] as const

// The router JSON-parses search values, so "?q=123" arrives as a number.
const text = z
  .union([z.string(), z.number()])
  .transform((v) => String(v).trim() || undefined)
  .optional()
  .catch(undefined)

const searchSchema = z.object({
  state: z.enum(ORDER_STATES).optional().catch(undefined),
  profile: z.enum(PROFILES).optional().catch(undefined),
  overdue: z.boolean().optional().catch(undefined),
  q: text,
})

export const Route = createFileRoute('/ops/')({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: async ({ deps }) => {
    const qs = new URLSearchParams()
    if (deps.state) qs.set('state', deps.state)
    if (deps.profile) qs.set('profile', deps.profile)
    if (deps.overdue) qs.set('overdue', '1')
    if (deps.q) qs.set('q', deps.q)
    const { orders } = await opsApi<{ orders: OpsOrderRow[] }>(`/ops/orders${qs.size ? `?${qs}` : ''}`)
    // The API sorts overdue first; keep that guarantee here even if it changes (stable sort).
    return [...orders].sort((a, b) => Number(b.overdue) - Number(a.overdue))
  },
  head: () => ({ meta: [{ title: 'Orders · Ops' }] }),
  component: OrdersPage,
  pendingComponent: () => <LoadingState label="Loading orders" />,
  errorComponent: ({ error }) => <ErrorState error={error} title="Could not load orders" />,
})

function OrdersPage() {
  const orders = Route.useLoaderData()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const router = useRouter()
  const [q, setQ] = useState(search.q ?? '')
  const searchRef = useRef<HTMLInputElement>(null)
  const now = Date.now()

  useEffect(() => setQ(search.q ?? ''), [search.q])
  useInterval(() => router.invalidate({ filter: (m) => m.routeId === Route.id }), 30_000)

  // "/" focuses the search box, like most ops tools.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !t.closest('input, textarea, select, [contenteditable]')) {
        e.preventDefault()
        searchRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const set = (patch: Partial<typeof search>) => navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })
  const onSearch = (e: FormEvent) => {
    e.preventDefault()
    set({ q: q.trim() || undefined })
  }
  const filtered = Boolean(search.state || search.profile || search.overdue || search.q)
  const overdueCount = orders.filter((o) => o.overdue).length

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <h1 className="mr-auto text-base font-semibold">
          Orders <span className="font-normal text-muted-foreground">({orders.length})</span>
          {overdueCount > 0 && <span className="ml-2 text-sm font-normal text-destructive">{overdueCount} overdue</span>}
        </h1>
        <form onSubmit={onSearch} role="search" className="flex items-end gap-1">
          <div className="flex flex-col gap-1">
            <Label htmlFor="orders-q" className="text-xs text-muted-foreground">
              Search <kbd className="rounded border border-border px-1 text-[10px]">/</kbd>
            </Label>
            <Input
              id="orders-q"
              ref={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name, domain, email, id"
              className="w-56"
            />
          </div>
          <Button type="submit" variant="outline" size="icon" aria-label="Search">
            <SearchIcon aria-hidden />
          </Button>
        </form>
        <div className="flex flex-col gap-1">
          <Label htmlFor="orders-state" className="text-xs text-muted-foreground">
            State
          </Label>
          <NativeSelect
            id="orders-state"
            value={search.state ?? ''}
            onChange={(e) => set({ state: (e.target.value || undefined) as typeof search.state })}
          >
            <NativeSelectOption value="">All states</NativeSelectOption>
            {ORDER_STATES.map((s) => (
              <NativeSelectOption key={s} value={s}>
                {STATE_LABELS[s]}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="orders-profile" className="text-xs text-muted-foreground">
            Profile
          </Label>
          <NativeSelect
            id="orders-profile"
            value={search.profile ?? ''}
            onChange={(e) => set({ profile: (e.target.value || undefined) as typeof search.profile })}
          >
            <NativeSelectOption value="">All profiles</NativeSelectOption>
            <NativeSelectOption value="classic">Classic</NativeSelectOption>
            <NativeSelectOption value="page_builder">Page builder</NativeSelectOption>
          </NativeSelect>
        </div>
        <Label className="h-8 cursor-pointer">
          <Checkbox checked={search.overdue ?? false} onCheckedChange={(v) => set({ overdue: v ? true : undefined })} />
          Overdue only
        </Label>
        {filtered && (
          <Button
            variant="ghost"
            onClick={() => navigate({ search: {}, replace: true })}
          >
            Clear filters
          </Button>
        )}
      </div>

      {orders.length === 0 ? (
        <Empty className="border border-border">
          <EmptyHeader>
            <EmptyTitle>No orders</EmptyTitle>
            <EmptyDescription>{filtered ? 'No order matches these filters.' : 'Orders appear here once visitors request a preview.'}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Business</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Profile</TableHead>
                <TableHead>In state</TableHead>
                <TableHead className="text-right">Cost / budget</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((o) => {
                const over = o.costUsd >= o.budgetUsd
                return (
                  <TableRow key={o.id} className={cn(o.overdue && 'bg-destructive/5')}>
                    <TableCell className="py-1.5">
                      <Link
                        to="/ops/orders/$orderId"
                        params={{ orderId: o.id }}
                        className="font-medium underline-offset-4 outline-none hover:underline focus-visible:underline"
                      >
                        {o.businessName}
                      </Link>
                      <div className="text-xs text-muted-foreground">{o.domain}</div>
                    </TableCell>
                    <TableCell className="py-1.5">
                      <StateBadge state={o.state} />
                    </TableCell>
                    <TableCell className="py-1.5 text-muted-foreground">{o.profile.replace('_', ' ')}</TableCell>
                    <TableCell className="py-1.5 tabular-nums">
                      <span className={cn(o.overdue && 'font-medium text-destructive')}>
                        {o.overdue && <AlertTriangleIcon className="mr-1 inline size-3.5 align-[-2px]" aria-hidden />}
                        {formatAge(o.stateEnteredAt, now)}
                        {o.overdue && ' · overdue'}
                      </span>
                      {o.timeoutAt && !o.overdue && (
                        <div className="text-xs text-muted-foreground">timeout {formatDateTime(o.timeoutAt)}</div>
                      )}
                    </TableCell>
                    <TableCell className={cn('py-1.5 text-right tabular-nums', over && 'font-medium text-destructive')}>
                      {formatUsd(o.costUsd)} <span className="text-muted-foreground">/ {formatUsd(o.budgetUsd)}</span>
                      {over && <span className="sr-only"> (over budget)</span>}
                    </TableCell>
                    <TableCell className="py-1.5 text-muted-foreground">{formatDateTime(o.createdAt)}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
